import React, { useEffect, useRef, useState } from "react";
import { MapPin, ChevronDown, X } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { fetchCountries, fetchRegions, fetchProvinces, searchAddresses, searchCities } from "../utils/geoApi";
import { sortCountriesByPriority } from "../utils/countryOrder";
import MAP_CONFIG from "../config/map-config";

const FLAGS = { ES: "🇪🇸", UY: "🇺🇾", US: "🇺🇸", AR: "🇦🇷", BR: "🇧🇷" };

const SELECT_CLASS = "w-full h-10 px-3 rounded-lg border border-[#E2E8F0] text-sm font-poppins bg-white focus:outline-none focus:ring-2 focus:ring-[#028CF8]/30 focus:border-[#028CF8]";

/**
 * Compact País → Región/Provincia → Ciudad picker embedded in the main search
 * bars (Home + Jobs). Unlike `LocationSelector` (job posting form), every
 * level here is optional — a candidate can search by country alone. Renders
 * either as a popover attached to a trigger (`inline=false`, desktop/mobile
 * sticky search bars) or as a plain in-flow panel (`inline=true`, used inside
 * the Jobs mobile Filtros drawer, which is already its own bottom sheet).
 */
export function CountrySearchField({ value, onChange, onLocate, inline = false, placeholder, testidPrefix = "country-field", triggerHeight = "h-11", triggerBg = "bg-transparent" }) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [countries, setCountries] = useState([]);
  const [regions, setRegions] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const ref = useRef(null);
  const flySeqRef = useRef(0);
  const cityDebounceRef = useRef(null);

  const { countryCode, admin1Code, admin1Name, admin2Code, admin2Name, city } = value || {};

  useEffect(() => { fetchCountries().then(setCountries).catch(() => {}); }, []);

  useEffect(() => {
    if (!countryCode) { setRegions([]); return; }
    fetchRegions(countryCode).then(setRegions).catch(() => setRegions([]));
  }, [countryCode]);

  useEffect(() => {
    if (countryCode !== "ES" || !admin1Code) { setProvinces([]); return; }
    fetchProvinces(countryCode, admin1Code).then(setProvinces).catch(() => setProvinces([]));
  }, [countryCode, admin1Code]);

  useEffect(() => {
    if (inline || !open) return;
    const onOutside = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, [inline, open]);

  // Cancels any pending debounced city geocode on unmount.
  useEffect(() => () => { if (cityDebounceRef.current) clearTimeout(cityDebounceRef.current); }, []);

  const countryMeta = countries.find((c) => c.code === countryCode);
  const level1Key = countryMeta?.level1_label_key;
  const needsAdmin2Step = countryCode === "ES" && provinces.length > 0;

  const countryFlyTarget = (code) => {
    const cfg = MAP_CONFIG.countryDefaults[code];
    return cfg ? { lat: cfg.center[0], lon: cfg.center[1], zoom: cfg.zoom, level: "country" } : null;
  };

  // Progressively resolves the deepest selected level (city > región/provincia
  // > país) into map coordinates via the existing backend geocoding endpoints
  // (no new external calls) and reports it up via `onLocate` — purely visual,
  // never touches the job search filters/results.
  const resolveLocate = async ({ cc, a1, a2, cty }) => {
    if (!onLocate) return;
    const seq = ++flySeqRef.current;
    if (!cc) { onLocate(null); return; }
    const fallback = countryFlyTarget(cc);
    const cityQuery = (cty || "").trim();
    const regionQuery = a2 || a1;
    try {
      if (cityQuery.length >= 3) {
        const results = await searchCities(cc, cityQuery, a1 || undefined);
        if (seq !== flySeqRef.current) return;
        if (results?.length) { onLocate({ lat: results[0].latitude, lon: results[0].longitude, zoom: 12, level: "city" }); return; }
      }
      if (regionQuery) {
        const results = await searchAddresses(regionQuery, cc);
        if (seq !== flySeqRef.current) return;
        if (results?.length) { onLocate({ lat: Number(results[0].lat), lon: Number(results[0].lon), zoom: a2 ? 9 : 8, level: "region" }); return; }
      }
      if (seq === flySeqRef.current) onLocate(fallback);
    } catch {
      if (seq === flySeqRef.current) onLocate(fallback);
    }
  };

  const pickCountry = (code) => {
    onChange({ countryCode: code, admin1Code: null, admin1Name: null, admin2Code: null, admin2Name: null, city: "" });
    if (cityDebounceRef.current) clearTimeout(cityDebounceRef.current);
    resolveLocate({ cc: code });
  };
  const clearCountry = (e) => {
    e.stopPropagation();
    onChange({ countryCode: null, admin1Code: null, admin1Name: null, admin2Code: null, admin2Name: null, city: "" });
    if (cityDebounceRef.current) clearTimeout(cityDebounceRef.current);
    flySeqRef.current++;
    if (onLocate) onLocate(null);
  };
  const pickAdmin1 = (e) => {
    const code = e.target.value;
    const region = regions.find((r) => r.code === code);
    onChange({ admin1Code: code || null, admin1Name: region?.name || null, admin2Code: null, admin2Name: null });
    if (cityDebounceRef.current) clearTimeout(cityDebounceRef.current);
    resolveLocate({ cc: countryCode, a1: region?.name || null, a2: null, cty: city });
  };
  const pickAdmin2 = (e) => {
    const code = e.target.value;
    const province = provinces.find((p) => p.code === code);
    onChange({ admin2Code: code || null, admin2Name: province?.name || null });
    if (cityDebounceRef.current) clearTimeout(cityDebounceRef.current);
    resolveLocate({ cc: countryCode, a1: admin1Name, a2: province?.name || null, cty: city });
  };
  const changeCity = (e) => {
    const val = e.target.value;
    onChange({ city: val });
    if (cityDebounceRef.current) clearTimeout(cityDebounceRef.current);
    cityDebounceRef.current = setTimeout(() => {
      resolveLocate({ cc: countryCode, a1: admin1Name, a2: admin2Name, cty: val });
    }, 500);
  };

  const summaryLabel = city || admin1Name || (countryMeta ? t(`geo.countryNames.${countryCode}`) : "");
  const summaryFlag = countryCode ? FLAGS[countryCode] : null;

  const panel = (
    <div data-testid={`${testidPrefix}-panel`} className={inline ? "space-y-3" : "space-y-3 p-3.5 bg-white rounded-2xl border border-[#E5E7EB] shadow-xl"}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold uppercase tracking-wide text-[#94A3B8] font-poppins">{t("geo.countryLabel")}</span>
        {countryCode && (
          <button type="button" data-testid={`${testidPrefix}-clear-country`} onClick={clearCountry}
            className="flex items-center gap-1 text-xs text-[#94A3B8] hover:text-[#028CF8] font-poppins">
            <X className="h-3 w-3" /> {t("geo.anyCountry")}
          </button>
        )}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {sortCountriesByPriority(countries.length ? countries : Object.keys(FLAGS).map((c) => ({ code: c })), t).map((c) => (
          <button key={c.code} type="button" data-testid={`${testidPrefix}-pill-${c.code}`}
            onClick={() => pickCountry(c.code)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-sm font-poppins font-medium transition-all ${
              countryCode === c.code ? "border-[#028CF8] bg-[#028CF8]/10 text-[#028CF8]" : "border-[#E5E7EB] text-[#334155] hover:border-[#028CF8]/40"
            }`}>
            <span>{FLAGS[c.code]}</span> {t(`geo.countryNames.${c.code}`)}
          </button>
        ))}
      </div>

      {countryCode && (
        <div className={`grid grid-cols-1 gap-2 ${needsAdmin2Step ? "sm:grid-cols-2" : ""}`}>
          <div>
            <label className="text-xs font-semibold text-slate-500 mb-1 block">{t(`geo.admin1LabelByKey.${level1Key}`)}</label>
            <select data-testid={`${testidPrefix}-admin1-select`} value={admin1Code || ""} onChange={pickAdmin1} className={SELECT_CLASS}>
              <option value="">{t("geo.anyRegion")}</option>
              {regions.map((r) => <option key={r.code} value={r.code}>{r.name}</option>)}
            </select>
          </div>
          {needsAdmin2Step && (
            <div>
              <label className="text-xs font-semibold text-slate-500 mb-1 block">{t("geo.admin2Label")}</label>
              <select data-testid={`${testidPrefix}-admin2-select`} value={admin2Code || ""} onChange={pickAdmin2} className={SELECT_CLASS}>
                <option value="">{t("geo.anyProvince")}</option>
                {provinces.map((p) => <option key={p.code} value={p.code}>{p.name}</option>)}
              </select>
            </div>
          )}
        </div>
      )}

      {countryCode && (
        <div>
          <label className="text-xs font-semibold text-slate-500 mb-1 block">{t("geo.cityLabel")}</label>
          <input data-testid={`${testidPrefix}-city-input`} type="text" value={city || ""} onChange={changeCity}
            placeholder={t("geo.cityPlaceholder")}
            className="w-full h-10 px-3 rounded-lg border border-[#E2E8F0] text-sm font-poppins bg-white focus:outline-none focus:ring-2 focus:ring-[#028CF8]/30 focus:border-[#028CF8]" />
        </div>
      )}

      {!inline && (
        <button type="button" data-testid={`${testidPrefix}-done-button`} onClick={() => setOpen(false)}
          className="w-full h-9 rounded-lg bg-[#028CF8] text-white text-sm font-semibold font-poppins">
          {t("geo.done")}
        </button>
      )}
    </div>
  );

  if (inline) return panel;

  return (
    <div className="relative" ref={ref}>
      <button type="button" data-testid={`${testidPrefix}-trigger`} onClick={() => setOpen((v) => !v)}
        className={`w-full ${triggerHeight} pl-9 pr-8 text-left text-base lg:text-sm font-poppins text-[#1A1A1A] ${triggerBg} rounded-xl border border-[#E5E7EB] focus:border-[#028CF8] focus:ring-2 focus:ring-[#028CF8]/15 transition-all relative`}>
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94A3B8] pointer-events-none" />
        <span className={`truncate block ${summaryLabel ? "" : "text-[#94A3B8]"}`}>
          {summaryFlag && <span className="mr-1.5">{summaryFlag}</span>}
          {summaryLabel || placeholder || t("geo.countryLabel")}
        </span>
        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94A3B8] pointer-events-none" />
      </button>
      {open && (
        <div className={`absolute z-30 mt-1.5 w-full max-w-full ${needsAdmin2Step ? "sm:w-[420px]" : "sm:w-[340px]"}`}>
          {panel}
        </div>
      )}
    </div>
  );
}
